import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const testimonialsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  const currentTestimonials = testimonials.slice(
    currentPage * testimonialsPerPage,
    (currentPage + 1) * testimonialsPerPage
  );

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-orange-100 rounded-full mb-4">
            <span className="text-orange-700 font-semibold text-sm">Testimonials</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-slate-600">
            Over 2,000 satisfied customers across Central Texas
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {currentTestimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id}
                className="bg-white border-2 border-slate-100 hover:border-cyan-400 hover:shadow-xl transition-all duration-300 group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-slate-700 text-base leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-cyan-400"
                    />
                    <div>
                      <div className="font-bold text-slate-900 group-hover:text-cyan-600 transition-colors duration-200">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-slate-500">{testimonial.location}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={prevPage}
                variant="outline"
                className="border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="flex gap-2">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(idx)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentPage === idx 
                        ? 'bg-cyan-500 w-8' 
                        : 'bg-slate-300 hover:bg-cyan-300'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={nextPage}
                variant="outline"
                className="border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          )}

          <p className="text-center text-slate-500 text-sm mt-6">
            Showing {currentPage * testimonialsPerPage + 1}-{Math.min((currentPage + 1) * testimonialsPerPage, testimonials.length)} of {testimonials.length} reviews
          </p>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: 'BBB Accredited', value: 'A+' },
            { label: 'Licensed & Insured', value: '✓' },
            { label: 'Years in Business', value: '20+' },
            { label: 'Customer Rating', value: '5.0★' }
          ].map((badge, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="text-3xl font-bold text-cyan-600 mb-2">{badge.value}</div>
              <div className="text-sm text-slate-600 font-medium">{badge.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
